import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useScrollLock } from '../../_utils/useScrollLock';
import ReactDOM from 'react-dom';
import VoucherCard from './VoucherCard';
import DesignerContextMenu from './DesignerContextMenu';
import DesignerRail from './DesignerRail';
import DesignerCategoryPanel from './DesignerCategoryPanel';
import DesignerTopContextBar from './DesignerTopContextBar';
import { isDarkColor } from './CustomCardScene';
import {
  defaultCustomConfig,
  normaliseCustomConfig,
  mintElementId,
  TEXT_DEFAULTS,
} from './data';
import '../shared/ui.css';

// CustomDesignerPanel — Canva-style full-screen canvas editor.
//
// Layout (CSS grid):
//                    col1 (rail)   col2 (panel)   col3 (canvas)
//   row1 top bar     ─────────────────────────────────────────────
//   row2 ctx bar     ─────────────────────────────────────────────
//   row3 main        [ Rail ]      [ Panel ]      [ Canvas       ]
//
// Mobile flips column 1 → bottom row 4 (horizontal bar), and panel
// becomes a bottom sheet. CSS handles the rearrangement; same JSX.

const mintCustomId = () => `custom-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 5)}`;
const SAMPLE_PREVIEW_VALUE = 50;

// Auto-generated name when the salon saves without typing one in.
// Format: "Voucher - 14 Jun 26" (today). If a design with that exact
// label already exists, append " (1)", " (2)", … until unique.
const buildAutoName = (existing = []) => {
  const d = new Date();
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const base = `Voucher - ${d.getDate()} ${months[d.getMonth()]} ${String(d.getFullYear()).slice(-2)}`;
  const names = new Set((existing || []).map((d2) => d2.label));
  if (!names.has(base)) return base;
  for (let i = 1; i < 100; i++) {
    const candidate = `${base} (${i})`;
    if (!names.has(candidate)) return candidate;
  }
  return base;
};

const CloseIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden>
    <path d="M3 3l8 8M11 3l-8 8" />
  </svg>
);
const UndoIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M6 4L2.5 7.5 6 11" />
    <path d="M2.5 7.5H10a3.5 3.5 0 0 1 0 7H8.5" />
  </svg>
);
const RedoIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M10 4l3.5 3.5L10 11" />
    <path d="M13.5 7.5H6a3.5 3.5 0 0 0 0 7h1.5" />
  </svg>
);

// ─── Undo / redo history over the design config ───
// A change pushes the previous config onto `past`. Rapid successive changes
// (a drag, holding down a stepper) within COALESCE_MS collapse into a single
// history entry so one undo reverts the whole gesture, not a pixel of it.
const COALESCE_MS = 450;
const HISTORY_LIMIT = 100;

const historyReducer = (h, action) => {
  switch (action.type) {
    case 'set': {
      const next = typeof action.updater === 'function' ? action.updater(h.present) : action.updater;
      if (next === h.present) return h;
      const coalesce = action.now - h.lastAt < COALESCE_MS;
      const past = coalesce ? h.past : [...h.past, h.present].slice(-HISTORY_LIMIT);
      return { past, present: next, future: [], lastAt: action.now };
    }
    case 'undo': {
      if (!h.past.length) return h;
      return {
        past: h.past.slice(0, -1),
        present: h.past[h.past.length - 1],
        future: [h.present, ...h.future],
        lastAt: 0,
      };
    }
    case 'redo': {
      if (!h.future.length) return h;
      return {
        past: [...h.past, h.present],
        present: h.future[0],
        future: h.future.slice(1),
        lastAt: 0,
      };
    }
    default:
      return h;
  }
};

const CustomDesignerPanel = ({
  design,
  existingDesigns = [],
  onClose,
  onSave,
  salonName,
  currency = '£',
  // Salon-owned assets surfaced by the Image panel's tabs so the salon
  // can drop in their own brand / work photos without re-uploading.
  // Either can be empty — the panel renders an empty-state in that case.
  salonImage = null,
  portfolio = [],
}) => {
  const isEdit = !!design;

  const [name, setName] = useState(design?.label || '');
  // Config lives behind an undo/redo history. `setConfig` keeps the exact
  // same (updater | value) signature as useState, so every call site is
  // unchanged; it just records history as a side effect.
  const [history, dispatchHistory] = useReducer(
    historyReducer,
    null,
    () => ({
      past: [],
      present: design?.config ? normaliseCustomConfig(design.config) : defaultCustomConfig(),
      future: [],
      lastAt: 0,
    })
  );
  const config = history.present;
  const setConfig = useCallback((updater) => {
    dispatchHistory({ type: 'set', updater, now: Date.now() });
  }, []);
  const undo = useCallback(() => dispatchHistory({ type: 'undo' }), []);
  const redo = useCallback(() => dispatchHistory({ type: 'redo' }), []);
  const canUndo = history.past.length > 0;
  const canRedo = history.future.length > 0;
  const [closing, setClosing] = useState(false);

  // Canvas + rail state
  const [activeCategory, setActiveCategory] = useState(null);
  // Selection is a set of ids (multi-select). Single-element affordances
  // (per-element toolbar, inline edit, resize/rotate handles) only engage
  // when EXACTLY one is selected; with 0 or 2+ selected, `selectedId` is
  // null and a multi-select toolbar (align / distribute / group) shows.
  const [selectedIds, setSelectedIds] = useState([]);
  const selectedId = selectedIds.length === 1 ? selectedIds[0] : null;
  // Live mirror so the (rarely-rebound) Esc-to-close effect can tell whether
  // there's a selection to clear first without re-subscribing on every change.
  const selectedIdsRef = useRef(selectedIds);
  selectedIdsRef.current = selectedIds;
  const [editingId, setEditingId] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);

  const blobUrlsRef = useRef([]);
  useEffect(() => () => {
    blobUrlsRef.current.forEach(u => URL.revokeObjectURL(u));
  }, []);

  // Shared ref-counted scroll lock: overlapping overlays must not restore
  // each other's locked styles.
  useScrollLock(true);

  // Esc to close
  useEffect(() => {
    // Escape only closes the designer when there's nothing to deselect first
    // (Canva-style: Esc deselects, then a second Esc closes). The selection
    // keyboard effect handles the deselect case.
    const onKey = (e) => {
      if (e.key === 'Escape' && !contextMenu && !selectedIdsRef.current.length) handleClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextMenu]);

  const handleClose = () => {
    if (closing) return;
    setClosing(true);
    setTimeout(() => onClose(), 180);
  };

  // ─── Card-level setters ───
  const set = (patch) => setConfig((prev) => ({ ...prev, ...patch }));

  const handleBgColor = (hex) => {
    set({
      bgColor: hex,
      ink: isDarkColor(hex) ? '#FFFFFF' : '#1A1A1E',
    });
  };

  const handleGradientColor = (key, hex) => {
    setConfig((prev) => {
      const grad = { ...prev.bgGradient, [key]: hex };
      const ink = isDarkColor(grad.from) || isDarkColor(grad.to) ? '#FFFFFF' : '#1A1A1E';
      return { ...prev, bgGradient: grad, ink };
    });
  };

  const onUploadPhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const url = URL.createObjectURL(file);
    blobUrlsRef.current.push(url);
    set({ bgPhoto: { url, focalX: 50, focalY: 50, zoom: 1 }, bgMode: 'photo' });
    e.target.value = '';
  };

  const onBgPhotoChange = (patch) =>
    setConfig((c) => ({ ...c, bgPhoto: { ...c.bgPhoto, ...patch } }));

  // ─── Element ops ───
  const updateElement = (id, patch) =>
    setConfig((c) => ({
      ...c,
      elements: c.elements.map(el => el.id === id ? { ...el, ...patch } : el),
    }));

  // Patch many elements in one history step (used by multi-move + align/
  // distribute). `patchById` is { [id]: partialElement }.
  const updateElements = (patchById) =>
    setConfig((c) => ({
      ...c,
      elements: c.elements.map(el => patchById[el.id] ? { ...el, ...patchById[el.id] } : el),
    }));

  // Jitter the drop position so consecutive adds don't stack on top of
  // each other in dead-centre. ±12% on each axis around the centre.
  const jitter = () => Math.round((Math.random() - 0.5) * 24);

  const addElement = (partial) => {
    const id = mintElementId();
    setConfig((c) => ({
      ...c,
      elements: [...c.elements, { id, x: 50 + jitter(), y: 50 + jitter(), scale: 1, ...partial }],
    }));
    return id;
  };

  const removeElement = (id) => {
    setConfig((c) => ({ ...c, elements: c.elements.filter(el => el.id !== id) }));
    setSelectedIds((ids) => ids.filter(x => x !== id));
    if (editingId === id) setEditingId(null);
  };

  const duplicateElement = (id) =>
    setConfig((c) => {
      const el = c.elements.find(e => e.id === id);
      if (!el) return c;
      const copy = { ...el, id: mintElementId(), x: (el.x ?? 50) + 5, y: (el.y ?? 50) + 5 };
      return { ...c, elements: [...c.elements, copy] };
    });

  const bringToFront = (id) =>
    setConfig((c) => {
      const el = c.elements.find(e => e.id === id);
      if (!el) return c;
      return { ...c, elements: [...c.elements.filter(e => e.id !== id), el] };
    });

  const sendToBack = (id) =>
    setConfig((c) => {
      const el = c.elements.find(e => e.id === id);
      if (!el) return c;
      return { ...c, elements: [el, ...c.elements.filter(e => e.id !== id)] };
    });

  // Incremental depth — swap with the adjacent element. Render order = array
  // order = paint order (all elements share the same z-index), so moving an
  // element later in the array brings it one step forward (on top), earlier
  // sends it one step back.
  const bringForward = (id) =>
    setConfig((c) => {
      const i = c.elements.findIndex(e => e.id === id);
      if (i < 0 || i === c.elements.length - 1) return c;
      const next = [...c.elements];
      [next[i], next[i + 1]] = [next[i + 1], next[i]];
      return { ...c, elements: next };
    });

  const sendBackward = (id) =>
    setConfig((c) => {
      const i = c.elements.findIndex(e => e.id === id);
      if (i <= 0) return c;
      const next = [...c.elements];
      [next[i], next[i - 1]] = [next[i - 1], next[i]];
      return { ...c, elements: next };
    });

  // ─── Multi-selection ops (align / distribute / group) ───
  // Align + distribute work on the elements' rendered bounding boxes (which
  // include rotation + scale), so we measure the live DOM rather than reason
  // about raw config. Each element's x/y is its centre as a % of the card, so
  // we set x/y from the target box centre.
  const measureSelection = () => {
    const card = document.querySelector('.v2-cd-canvas-wrap .v2-card');
    if (!card) return [];
    const cr = card.getBoundingClientRect();
    if (!cr.width || !cr.height) return [];
    return selectedIds.map((id) => {
      const node = document.querySelector(`.v2-cc-el[data-el-id="${CSS.escape(id)}"]`);
      if (!node) return null;
      const r = node.getBoundingClientRect();
      return {
        id,
        left: (r.left - cr.left) / cr.width * 100,
        right: (r.right - cr.left) / cr.width * 100,
        top: (r.top - cr.top) / cr.height * 100,
        bottom: (r.bottom - cr.top) / cr.height * 100,
        w: r.width / cr.width * 100,
        h: r.height / cr.height * 100,
      };
    }).filter(Boolean);
  };

  const alignSelected = (edge) => {
    const m = measureSelection();
    if (m.length < 2) return;
    const patch = {};
    const minLeft = Math.min(...m.map(e => e.left));
    const maxRight = Math.max(...m.map(e => e.right));
    const minTop = Math.min(...m.map(e => e.top));
    const maxBottom = Math.max(...m.map(e => e.bottom));
    m.forEach((e) => {
      if (edge === 'left')    patch[e.id] = { x: minLeft + e.w / 2 };
      if (edge === 'hcenter') patch[e.id] = { x: (minLeft + maxRight) / 2 };
      if (edge === 'right')   patch[e.id] = { x: maxRight - e.w / 2 };
      if (edge === 'top')     patch[e.id] = { y: minTop + e.h / 2 };
      if (edge === 'vcenter') patch[e.id] = { y: (minTop + maxBottom) / 2 };
      if (edge === 'bottom')  patch[e.id] = { y: maxBottom - e.h / 2 };
    });
    updateElements(patch);
  };

  // Distribute = equal gaps between bounding boxes, keeping the outermost
  // two fixed. Needs 3+ elements to be meaningful.
  const distributeSelected = (axis) => {
    const m = measureSelection();
    if (m.length < 3) return;
    const startKey = axis === 'x' ? 'left' : 'top';
    const endKey = axis === 'x' ? 'right' : 'bottom';
    const sizeKey = axis === 'x' ? 'w' : 'h';
    const sorted = [...m].sort((a, b) => a[startKey] - b[startKey]);
    const spanStart = sorted[0][startKey];
    const spanEnd = sorted[sorted.length - 1][endKey];
    const totalSize = sorted.reduce((s, e) => s + e[sizeKey], 0);
    const gap = (spanEnd - spanStart - totalSize) / (sorted.length - 1);
    let cursor = spanStart;
    const patch = {};
    sorted.forEach((e) => {
      const centre = cursor + e[sizeKey] / 2;
      patch[e.id] = axis === 'x' ? { x: centre } : { y: centre };
      cursor += e[sizeKey] + gap;
    });
    updateElements(patch);
  };

  const groupSelected = () => {
    if (selectedIds.length < 2) return;
    const gid = `g-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 5)}`;
    setConfig((c) => ({
      ...c,
      elements: c.elements.map(el => selectedIds.includes(el.id) ? { ...el, groupId: gid } : el),
    }));
  };

  const ungroupSelected = () =>
    setConfig((c) => ({
      ...c,
      elements: c.elements.map((el) => {
        if (!selectedIds.includes(el.id)) return el;
        const { groupId, ...rest } = el;
        return rest;
      }),
    }));

  const duplicateSelected = () => {
    const sel = config.elements.filter(el => selectedIds.includes(el.id));
    if (!sel.length) return;
    const newIds = [];
    const copies = sel.map((el) => {
      const nid = mintElementId();
      newIds.push(nid);
      return { ...el, id: nid, x: (el.x ?? 50) + 4, y: (el.y ?? 50) + 4 };
    });
    setConfig((c) => ({ ...c, elements: [...c.elements, ...copies] }));
    setSelectedIds(newIds);
  };

  const removeSelected = () => {
    if (!selectedIds.length) return;
    setConfig((c) => ({ ...c, elements: c.elements.filter(el => !selectedIds.includes(el.id)) }));
    setSelectedIds([]);
    setEditingId(null);
  };

  // ─── Rail / panel actions ───
  const onAddText = (overrides = {}) => {
    const id = addElement({
      type: 'text',
      content: '',
      font: config.fontFamily,
      color: config.ink,
      ...TEXT_DEFAULTS,
      ...overrides,
    });
    setSelectedIds([id]);
    setEditingId(id);
    setActiveCategory(null);
  };

  const onAddPresetText = (preset) => {
    onAddText({
      content: preset.label,
      bold: preset.bold,
      italic: preset.italic,
      fontSize: preset.fontSize,
    });
  };

  const onAddShape = (shape) => {
    const id = addElement({
      type: 'shape',
      shape,
      scale: 1,
      fill: config.ink,
      // Rectangles look better with a hint of rounding by default; other
      // shapes start sharp. Tunable per shape via the Roundness control.
      radius: shape === 'rect' ? 14 : 0,
    });
    setSelectedIds([id]);
    // Keep the Shapes panel open on web so the salon can drop several
    // shapes in a row without re-opening it each time. On mobile the
    // bottom sheet covers the canvas so we still close it.
    if (window.innerWidth < 768) setActiveCategory(null);
  };

  const onAddImage = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const url = URL.createObjectURL(file);
    blobUrlsRef.current.push(url);
    const id = addElement({ type: 'image', src: url });
    setSelectedIds([id]);
    setActiveCategory(null);
    e.target.value = '';
  };

  // Same as onAddImage but for assets the salon already owns (portfolio
  // photo, salon brand image) — we have a URL directly, no File object
  // to read, no blob URL to track for revocation.
  const onAddImageFromUrl = (url) => {
    if (!url) return;
    const id = addElement({ type: 'image', src: url });
    setSelectedIds([id]);
    if (window.innerWidth < 768) setActiveCategory(null);
  };

  const hasAmount = config.elements.some(el => el.type === 'amount');
  const onAddAmount = () => {
    if (hasAmount) return;
    const id = addElement({ type: 'amount', font: config.fontFamily, color: config.ink, fontSize: 48 });
    setSelectedIds([id]);
    setActiveCategory(null);
  };

  // ─── Scene event handlers ───
  // Ids of every element sharing the group of `id` (or just [id] if ungrouped).
  // Selecting one member of a group selects the whole group.
  const groupMemberIds = (id) => {
    const el = config.elements.find(e => e.id === id);
    if (!el?.groupId) return [id];
    return config.elements.filter(e => e.groupId === el.groupId).map(e => e.id);
  };

  const handleSelect = (id, opts = {}) => {
    if (id == null) { setSelectedIds([]); return; }
    const ids = groupMemberIds(id);
    if (opts.additive) {
      // Shift/Cmd-click toggles this element (or its group) in the selection.
      setSelectedIds((cur) => {
        const allIn = ids.every(i => cur.includes(i));
        return allIn ? cur.filter(i => !ids.includes(i)) : [...new Set([...cur, ...ids])];
      });
    } else {
      setSelectedIds(ids);
    }
    if (id !== editingId) setEditingId(null);
  };

  const handleDoubleClick = (id) => {
    setSelectedIds([id]);
    setEditingId(id);
  };

  const handleContextMenuOpen = (id, x, y) => {
    setSelectedIds([id]);
    setContextMenu({ id, x, y });
  };

  const handleCommitEdit = () => setEditingId(null);

  // When the salon deselects (or selection becomes null for any
  // reason), close any open per-element edit panel so the side panel
  // doesn't show a stale "Text colour" with no target.
  useEffect(() => {
    if (!selectedId && (activeCategory === 'edit-color' || activeCategory === 'edit-stroke' || activeCategory === 'edit-font')) {
      setActiveCategory(null);
    }
  }, [selectedId, activeCategory]);

  // ─── Keyboard: delete selection · group/ungroup · Esc clears ───
  useEffect(() => {
    const onKey = (e) => {
      if (editingId) return;
      const tag = (e.target?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
      const mod = e.metaKey || e.ctrlKey;
      if (mod && e.key.toLowerCase() === 'g') {
        e.preventDefault();
        if (e.shiftKey) ungroupSelected(); else groupSelected();
        return;
      }
      if (!selectedIds.length) return;
      if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        removeSelected();
      }
      if (e.key === 'Escape') setSelectedIds([]);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIds, editingId]);

  // ─── Keyboard: undo / redo ───
  useEffect(() => {
    const onKey = (e) => {
      const mod = e.metaKey || e.ctrlKey;
      if (!mod) return;
      const k = e.key.toLowerCase();
      // Leave native text undo alone while typing in a field or inline-editing.
      const tag = (e.target?.tagName || '').toLowerCase();
      if (editingId || tag === 'input' || tag === 'textarea' || e.target?.isContentEditable) return;
      if (k === 'z') {
        e.preventDefault();
        if (e.shiftKey) redo(); else undo();
      } else if (k === 'y') {
        e.preventDefault();
        redo();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [undo, redo, editingId]);

  // ─── Save ───
  // No name-required validation any more — empty name auto-generates
  // "Voucher - 14 Jun 26" (and increments " (1)", " (2)" for collisions
  // within the same day). The salon can rename later from the library.
  const handleSave = () => {
    const finalName = name.trim() || buildAutoName(existingDesigns);
    onSave({
      id: design?.id || mintCustomId(),
      aesthetic: 'custom',
      theme: design?.theme || 'charcoal',
      occasion: null,
      label: finalName,
      available: design?.available !== false,
      isCustom: true,
      config,
    });
    handleClose();
  };

  // ─── Preview voucher ───
  const previewVoucher = useMemo(() => ({
    name: name || 'Gift Card',
    template: 'signature',
    theme: 'charcoal',
    value: SAMPLE_PREVIEW_VALUE,
    promo: null,
    customDesign: config,
  }), [name, config]);

  // ─── Context menu items ───
  const contextMenuItems = useMemo(() => {
    if (!contextMenu) return [];
    const id = contextMenu.id;
    const idx = config.elements.findIndex(e => e.id === id);
    const atFront = idx === config.elements.length - 1;
    const atBack = idx <= 0;
    return [
      { label: 'Bring to front', onClick: () => bringToFront(id),  disabled: atFront },
      { label: 'Bring forward',  onClick: () => bringForward(id),  disabled: atFront },
      { label: 'Send backward',  onClick: () => sendBackward(id),  disabled: atBack },
      { label: 'Send to back',   onClick: () => sendToBack(id),    disabled: atBack },
      { divider: true },
      { label: 'Duplicate',      onClick: () => duplicateElement(id) },
      { divider: true },
      { label: 'Delete',         danger: true, onClick: () => removeElement(id) },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextMenu, config.elements]);

  // Selected element for the context bar (single selection only)
  const selectedEl = useMemo(
    () => config.elements.find(el => el.id === selectedId) || null,
    [config.elements, selectedId]
  );

  // Multi-selection info for the align / group toolbar.
  const selectedEls = useMemo(
    () => config.elements.filter(el => selectedIds.includes(el.id)),
    [config.elements, selectedIds]
  );
  const isGroupSelected = selectedEls.length >= 2
    && selectedEls.every(e => e.groupId && e.groupId === selectedEls[0].groupId);

  const onCtxElementChange = (patch) => {
    if (selectedEl) updateElement(selectedEl.id, patch);
  };

  const modal = (
    <>
      <div
        className={`v2-cd-backdrop ${closing ? 'is-closing' : ''}`}
        onClick={handleClose}
      />
      <div
        className={[
          'voucher-v2',
          'v2-cd-modal',
          closing ? 'is-closing' : '',
          activeCategory ? 'has-panel' : '',
        ].join(' ').trim()}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={isEdit ? 'Edit custom design' : 'New custom design'}
      >
        {/* Top bar */}
        <header className="v2-cd-topbar">
          <button type="button" className="v2-cd-iconbtn" onClick={handleClose} aria-label="Close">
            <CloseIcon />
          </button>
          <div className="v2-cd-history">
            <button
              type="button"
              className="v2-cd-iconbtn"
              onClick={undo}
              disabled={!canUndo}
              aria-label="Undo"
              title="Undo (⌘Z)"
            >
              <UndoIcon />
            </button>
            <button
              type="button"
              className="v2-cd-iconbtn"
              onClick={redo}
              disabled={!canRedo}
              aria-label="Redo"
              title="Redo (⇧⌘Z)"
            >
              <RedoIcon />
            </button>
          </div>
          <div className="v2-cd-topbar-name">
            <input
              type="text"
              className="v2-cd-name-input"
              placeholder="Untitled design"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="v2-cd-topbar-actions">
            <button type="button" className="sp-btn-secondary" onClick={handleClose}>Cancel</button>
            <button type="button" className="sp-btn-primary" onClick={handleSave}>
              {isEdit ? 'Save changes' : 'Save'}
            </button>
          </div>
        </header>

        {/* Context bar — per-element controls for a single selection, or the
            align / distribute / group toolbar when 2+ are selected. */}
        <div className="v2-cd-ctxbar-host">
          <DesignerTopContextBar
            element={selectedEl}
            multiCount={selectedIds.length}
            isGroup={isGroupSelected}
            onAlign={alignSelected}
            onDistribute={distributeSelected}
            onGroup={groupSelected}
            onUngroup={ungroupSelected}
            onDuplicateSelection={duplicateSelected}
            onDeleteSelection={removeSelected}
            onChange={onCtxElementChange}
            onOpenFontPanel={() => setActiveCategory('edit-font')}
            onOpenColorPanel={() => setActiveCategory('edit-color')}
            onOpenStrokePanel={() => setActiveCategory('edit-stroke')}
          />
        </div>

        {/* Left rail (web) / bottom bar (mobile) */}
        <DesignerRail
          active={activeCategory}
          onSelect={setActiveCategory}
          currency={currency}
        />

        {/* Category panel — slides out from rail (or up from bottom) */}
        <DesignerCategoryPanel
          category={activeCategory}
          onClose={() => setActiveCategory(null)}
          // for the edit-* categories
          selectedEl={selectedEl}
          onElementChange={updateElement}
          // text
          onAddText={onAddText}
          onAddPresetText={onAddPresetText}
          // shapes
          onAddShape={onAddShape}
          // amount
          onAddAmount={onAddAmount}
          hasAmount={hasAmount}
          currency={currency}
          // image — direct upload, salon brand image, and the salon's
          // portfolio of work photos. The picker UI renders three tabs.
          onAddImage={onAddImage}
          onAddImageFromUrl={onAddImageFromUrl}
          salonImage={salonImage}
          portfolio={portfolio}
          // background
          config={config}
          onChange={set}
          onChangeBgColor={handleBgColor}
          onChangeGradientColor={handleGradientColor}
          onUploadPhoto={onUploadPhoto}
        />

        {/* Canvas */}
        <main className="v2-cd-canvas-area" onClick={() => setActiveCategory(null)}>
          <div className="v2-cd-canvas-wrap" onClick={(e) => e.stopPropagation()}>
            <VoucherCard
              voucher={previewVoucher}
              aesthetic="custom"
              salonName={salonName}
              currency={currency}
              showPromo={false}
              interactive
              selectedId={selectedId}
              selectedIds={selectedIds}
              editingId={editingId}
              onElementSelect={handleSelect}
              onElementChange={updateElement}
              onElementsChange={updateElements}
              onElementDuplicate={duplicateElement}
              onElementRemove={removeElement}
              onElementDoubleClick={handleDoubleClick}
              onElementContextMenu={handleContextMenuOpen}
              onElementCommitEdit={handleCommitEdit}
              onBgPhotoChange={onBgPhotoChange}
            />
          </div>
        </main>

        {/* Context menu (right-click) */}
        {contextMenu && (
          <DesignerContextMenu
            x={contextMenu.x}
            y={contextMenu.y}
            items={contextMenuItems}
            onClose={() => setContextMenu(null)}
          />
        )}
      </div>
    </>
  );

  return ReactDOM.createPortal(modal, document.body);
};

export default CustomDesignerPanel;
